/*
 * Project: bellcloud
 * File: packages/ui/layout/web/TopBar.tsx
 * Author: SWAN
 * Created: 2025-08-04 11:23:16
 * Description: Unified TopBar with MenuPath, HierarchicalSelector, and user controls
 */

import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme';
import { Flex } from '@/components/ui';
import { MenuPath } from '@/components/ui/common/MenuPath';
import { HierarchicalSelectorContainer } from '@frontend/src/components/common/HierarchicalSelectorContainer';
import { useMenuStore } from '@/store/menuStore';
import { useMobileNavLayout } from '@/hooks/useMobileNavLayout';
import { i18nKeys } from '@/i18n/i18nKeyGen';

const MenuToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  margin-right: ${({ theme }) => theme.spacing[2]}px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.default};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surface.control.emphasis};
  }
`;

export const TopBar: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const isMobileNav = useMobileNavLayout();
  const toggleSidebar = useMenuStore(s => s.toggleSidebar);
  const isSidebarOpen = useMenuStore(s => s.isSidebarOpen);

  return (
    <Flex
      direction="column"
      align="start"
      style={{
        width: '100%',
        padding: `${theme.spacing[6]}px`,
        gap: `${theme.spacing[4]}px`,
      }}
    >
      <Flex direction="row" align="start" style={{ width: '100%' }}>
        {isMobileNav && (
          <MenuToggleButton
            type="button"
            aria-label={t(i18nKeys.common.nav_open_menu)}
            aria-expanded={isSidebarOpen}
            onClick={() => toggleSidebar()}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </MenuToggleButton>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <MenuPath />
        </div>
      </Flex>
      <HierarchicalSelectorContainer />
    </Flex>
  );
};
